/**
 * CrmDealTagsFilter - Filtro de Tags de Negócios (multi-seleção)
 * 
 * Permite selecionar múltiplas tags do tenant.
 * Trigger mostra resumo (N tags) com miniaturas coloridas.
 * 
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-04-23 v1.0.0 - Implementação inicial para listagem de Negócios
 */

import React, { useState } from 'react';
import { Check, ChevronDown, Tag, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useDealTags } from '@/hooks/useDealTags';
import { useAuth } from '@/hooks/useAuth';

/**
 * Props do componente
 * @param value - Array de IDs de tags selecionadas
 * @param onChange - Callback de mudança
 * @param className - Classes CSS adicionais
 */
interface CrmDealTagsFilterProps {
  value: string[];
  onChange: (tagIds: string[]) => void;
  className?: string;
}

/**
 * Filtro de tags multi-seleção
 */
export function CrmDealTagsFilter({ value, onChange, className }: CrmDealTagsFilterProps) {
  const [open, setOpen] = useState(false);
  const { currentTenant } = useAuth();
  const { data: tags = [] } = useDealTags(currentTenant?.id);

  // Toggle individual de tag (multi-seleção)
  const toggleTag = (tagId: string) => {
    if (value.includes(tagId)) {
      onChange(value.filter(id => id !== tagId));
    } else {
      onChange([...value, tagId]);
    }
  };

  // Limpa todas as seleções
  const clearAll = () => {
    onChange([]);
  };

  // Tags selecionadas (até 3 mostradas como miniaturas)
  const selectedTags = tags.filter(t => value.includes(t.id));
  const previewTags = selectedTags.slice(0, 3);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5 font-normal border-0 bg-background rounded-full', className)}
        >
          <Tag className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0 text-muted-foreground" />
          {selectedTags.length > 0 ? (
            <span className="flex items-center gap-1 truncate">
              <span className="flex items-center -space-x-1">
                {previewTags.map(t => (
                  <span
                    key={t.id}
                    className="w-2.5 h-2.5 rounded-full border border-background shrink-0"
                    style={{ backgroundColor: t.color || '#94a3b8' }}
                  />
                ))}
              </span>
              <span className="truncate">{selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''}</span>
            </span>
          ) : (
            <span className="truncate">Tags</span>
          )}
          <ChevronDown className="h-3 w-3 opacity-50 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar tag..." />
          <CommandList>
            <CommandEmpty>Nenhuma tag encontrada.</CommandEmpty>
            <CommandGroup heading="Tags">
              {tags.map(tag => {
                const isSelected = value.includes(tag.id);
                return (
                  <CommandItem
                    key={tag.id}
                    value={tag.name}
                    onSelect={() => toggleTag(tag.id)}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        'mr-2 h-4 w-4 rounded border flex items-center justify-center',
                        isSelected ? 'border-primary bg-primary' : 'border-muted'
                      )}
                    >
                      {isSelected && <Check className="h-2.5 w-2.5 text-primary-foreground" />}
                    </div>
                    <span
                      className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                      style={{ backgroundColor: tag.color || '#94a3b8' }}
                    />
                    <span className="truncate flex-1">{tag.name}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
          {selectedTags.length > 0 && (
            <div className="border-t p-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full h-7 text-xs text-muted-foreground"
                onClick={clearAll}
              >
                <X className="h-3 w-3 mr-1" />
                Limpar seleção
              </Button>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
